// src/utils/url.ts

/**
 * Проверяет, является ли переданная строка абсолютным URL-адресом.
 * @param url Строка для проверки.
 * @returns true, если строка является валидным абсолютным URL, иначе false.
 */
const isAbsoluteURL = (url: string): boolean => {
	// Мы полагаемся на нативный конструктор URL. Он выбросит исключение,
	// если строка не является полным, самостоятельным URL (например, для "/path" будет ошибка).
	try {
		new URL(url);
		return true;
	} catch (e) {
		return false;
	}
};

/**
 * Безопасно объединяет базовый URL (или путь) с дополнительным путем.
 * Эта функция корректно обрабатывает как абсолютные URL в качестве базы,
 * так и относительные пути, а также заботится о правильной расстановке слешей.
 *
 * @param base - Базовый URL или путь. Может быть абсолютным ('https://example.com/api')
 *               или относительным ('/game', '/', '').
 * @param path - Дополнительный путь для объединения (например, '/users/1' или 'profile').
 * @returns Новая строка с объединенным URL/путем.
 *
 * @example
 * // С абсолютным URL в качестве базы
 * joinURL('https://api.example.com/v1', '/users')      // -> 'https://api.example.com/v1/users'
 * joinURL('https://api.example.com/v1/', 'users')     // -> 'https://api.example.com/v1/users'
 *
 * @example
 * // С относительным путем в качестве базы
 * joinURL('/game', '/api/tunnel') // -> '/game/api/tunnel'
 * joinURL('/game/', 'api/tunnel')  // -> '/game/api/tunnel'
 * joinURL('', '/profile')         // -> '/profile'
 * joinURL('/', '/settings')       // -> '/settings'
 */
export const joinURL = (base: string, path: string): string => {
	// Случай 1: `base` является полным, абсолютным URL.
	// Это самый надежный способ. Используем встроенный конструктор URL,
	// который сам корректно обработает все слеши.
	if (isAbsoluteURL(base)) {
		return new URL(path, base).toString();
	}

	// Случай 2: `base` является относительным путем.
	// Здесь мы не можем использовать `new URL()`, поэтому выполняем
	// конкатенацию строк с ручной нормализацией слешей.

	// Убираем слеш в конце `base` и слеш в начале `path`,
	// а затем соединяем их одним гарантированным слешем.
	// Это самый надежный способ избежать двойных слешей или их отсутствия.
	const cleanedBase = base.replace(/\/$/, "");
	const cleanedPath = path.replace(/^\//, "");

	const result = `${cleanedBase}/${cleanedPath}`;

	// Особый случай: если base был '/', то cleanedBase станет '',
	// и результат будет '/path', что неверно для некоторых случаев.
	// Например `joinURL('/', '/api')` даст `//api`.
	// Поэтому мы дополнительно нормализуем множественные слеши в один.
	if (result.startsWith("//")) {
		return result.substring(1);
	}

	return result;
};
